import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ProjectSelectOverlay from '@/sub-luca-plugin/components/project-select-overlay';
import { IReturnConfig } from '@/sub-luca-plugin/components/project-select-overlay/helper';
import { SubjectArrowIcon } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { query_projects_of_questions, query_projects_of_collects } from '@/sub-luca-plugin/apis';
import { IProjectConfig, IProjectIds } from '@/sub-luca-plugin/types';
import './index.scss';

interface IProps {
    type: 'my_question' | 'my_collect';
    initIds: IProjectIds;
    onChange?: (params: IProjectIds, isInit?: boolean) => void;
}

const ProjectFilter = ({ type, initIds, onChange }: IProps) => {
    // 筛选的项目科目是否是最后一级
    const isLastChildConfig = useRef(true);
    const determinedIds = useRef<IProjectIds>({});
    const [projects, setProjects] = useState<IProjectConfig[]>([]);
    const [visible, setVisible] = useState(false);
    const [chooseProjectStr, setChooseProjectStr] = useState('全部');

    // 初始化项目
    const initProjectAndSubject = async () => {
        const { result } = type === 'my_question' ? await query_projects_of_questions({}) : await query_projects_of_collects({});
        let chooseStr = '全部';
        let projectList: IProjectConfig[] = [];
        if (result?.length) {
            projectList =
                (result.map(item => {
                    if (item.pid === initIds?.projectId) {
                        chooseStr = item.name;
                        determinedIds.current.projectId = initIds.projectId;
                        isLastChildConfig.current = !item.subjects?.length;
                    }
                    const subjects =
                        item.subjects?.map(child => {
                            if (child.sid === initIds?.subjectId) {
                                chooseStr += `-${child.name}`;
                                determinedIds.current.subjectId = initIds.subjectId;
                                isLastChildConfig.current = !child.tlSubjects?.length;
                            }
                            const tlSubjects =
                                child.tlSubjects?.map(son => {
                                    if (son.tsid === initIds?.twoLevelSubjectId) {
                                        chooseStr += `-${son.name}`;
                                        determinedIds.current.twoLevelSubjectId = initIds.twoLevelSubjectId;
                                        isLastChildConfig.current = true;
                                    }
                                    return son;
                                }) || [];
                            if (!tlSubjects.length) return child;
                            return { ...child, tlSubjects: [{ name: '全部', tsid: -2 }, ...tlSubjects] };
                        }) || [];
                    if (!subjects.length) return item;
                    return { ...item, subjects: [{ name: '全部', sid: -2 }, ...subjects] };
                }) as any) || [];
        }
        onChange?.(determinedIds.current);
        setChooseProjectStr(chooseStr);
        setProjects([{ name: '全部', pid: -2 } as any].concat(projectList));
    };

    const handleReceiveConfig = (config: IReturnConfig) => {
        // console.log(config);
        isLastChildConfig.current = true;
        const { projectName, projectId, subjectName, subjectId, twoLevelSubjectName, twoLevelSubjectId } = config;
        determinedIds.current = { projectId, subjectId, twoLevelSubjectId };
        let str = projectName || '';
        if (subjectName && subjectName !== '全部') str += `-${subjectName}`;
        if (twoLevelSubjectName && twoLevelSubjectName !== '全部') str += `-${twoLevelSubjectName}`;
        onChange?.({
            projectId: projectId === -2 ? undefined : projectId,
            subjectId: subjectId === -2 ? undefined : subjectId,
            twoLevelSubjectId: twoLevelSubjectId === -2 ? undefined : twoLevelSubjectId,
        });
        setChooseProjectStr(str);
        setVisible(false);
    };

    const handleSelectProject = () => {
        if (isLastChildConfig.current) {
            isLastChildConfig.current = false;
            const newDeterminedIds = { ...determinedIds.current };
            if (newDeterminedIds.twoLevelSubjectId) {
                delete newDeterminedIds.twoLevelSubjectId;
            } else if (newDeterminedIds.subjectId) {
                delete newDeterminedIds.subjectId;
            } else if (newDeterminedIds.projectId) {
                delete newDeterminedIds.projectId;
            }
            determinedIds.current = newDeterminedIds;
        }
        setVisible(true);
    };

    const handleClose = useCallback(() => {
        setVisible(false);
    }, []);

    useEffect(() => {
        initProjectAndSubject();
    }, []);

    return (
        <div className="project-filter">
            {visible && (
                <ProjectSelectOverlay
                    className="project-filter__select"
                    projects={projects}
                    determinedIds={determinedIds.current}
                    hideBack={true}
                    allowChangeDeterminedIds
                    onReturnConfig={handleReceiveConfig}
                    onClose={handleClose}
                />
            )}
            {!!projects.length && (
                <div className="project-filter__info" onClick={handleSelectProject}>
                    <span className="project-filter__info-str">{chooseProjectStr}</span>
                    <SubjectArrowIcon />
                </div>
            )}
        </div>
    );
};

export default memo(ProjectFilter);
